import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserData } from "../../contexts/user-data-context";
import { useAnalytics } from "../../lib/analytics";
import type { OnboardingAnswers, UserData } from "../../types/onboarding";
import { Loader } from "../loading";
import { OnboardingWizard } from "../onboarding/OnboardingWizard";
import { reconstructCheckedStepIds } from "../onboarding/onboardingSave";
import styles from "./ProfileJourneyWizard.module.css";

interface ProfileJourneyWizardProps {
	startScreen: "status" | "role" | "journey";
	onClose: () => void;
}

// Full-screen overlay that runs the onboarding status→cycle segment for profile
// editing. Seeds the wizard from the user's saved details (and reconstructed step
// progress, which isn't part of /api/user) and refreshes data on completion.
export function ProfileJourneyWizard({
	startScreen,
	onClose,
}: ProfileJourneyWizardProps) {
	const { userData, refetchUserData } = useUserData();
	const { logProfile } = useAnalytics();
	const queryClient = useQueryClient();
	const [initialStepIds, setInitialStepIds] = useState<string[] | null>(null);
	const hasInitialized = useRef(false);

	const details = (userData?.details ?? null) as UserData | null;

	useEffect(() => {
		// Wait for details before initializing: if it's still loading at mount,
		// seeding now would lock initialStepIds to [] and miss existing step
		// progress. The effect re-runs when details arrives (it's a dependency).
		if (hasInitialized.current || !details) return;
		hasInitialized.current = true;
		logProfile("journey_edit_started", { start_screen: startScreen });
		const load = async () => {
			const ids = await reconstructCheckedStepIds(
				details.fertilityStatus ? String(details.fertilityStatus) : undefined,
				details.fertilityRole ? String(details.fertilityRole) : undefined,
				Array.isArray(details.fertilityJourneyTypes)
					? details.fertilityJourneyTypes
					: [],
			);
			setInitialStepIds(ids);
		};
		void load();
	}, [details, logProfile, startScreen]);

	const handleSegmentComplete = async (_answers: OnboardingAnswers) => {
		logProfile("journey_edit_completed", { start_screen: startScreen });
		// Close FIRST so the wizard unmounts before the refresh below. The refresh
		// goes through the api client, which drives the global (centered) loader;
		// keeping the wizard mounted during it would stack two loaders on screen.
		onClose();
		// Per-screen saves already wrote to the DB; refresh both the React Query
		// cache and the user-data context so the profile rows reflect new values.
		// Leave the session-immutable user-status query alone (same guard as
		// App.tsx) — invalidating ["user","status",sub] would re-derive the isD2C
		// gating mid-session.
		await queryClient.invalidateQueries({
			predicate: (q) =>
				!(q.queryKey[0] === "user" && q.queryKey[1] === "status"),
		});
		await refetchUserData();
	};

	// Render through a portal at <body> so the fixed full-screen overlay escapes
	// any transformed/contained ancestor in the Profile page tree (which would
	// otherwise confine `position: fixed` to the page content area and let the
	// app header / nav overlap the wizard). This gives the onboarding-style
	// full-screen takeover on every platform.
	if (initialStepIds === null) {
		return createPortal(
			<div className={styles.loading}>
				<Loader size={128} />
			</div>,
			document.body,
		);
	}

	return createPortal(
		<div className={styles.overlay}>
			<OnboardingWizard
				userData={details}
				initialStepIds={initialStepIds}
				onComplete={async () => {}}
				segment={{
					startScreen,
					onExit: onClose,
					onSegmentComplete: handleSegmentComplete,
				}}
			/>
		</div>,
		document.body,
	);
}
